import type { Message } from "@roqeet/core";

export interface UsageTotals {
  turns: number;
  prompt: number;
  completion: number;
  reasoning: number;
  total: number;
  costUsd: number;
  estimated: number;
}

export interface UsageRow extends UsageTotals {
  date: string;
}

export interface UsageBreakdownRow extends UsageTotals {
  key: string;
  label: string;
}

export interface UsageLedger {
  daily: UsageRow[];
  models: UsageBreakdownRow[];
  providers: UsageBreakdownRow[];
  pricingSources: UsageBreakdownRow[];
}

export const UNKNOWN_MODEL_LABEL = "\ubaa8\ub378 \uae30\ub85d \uc5c6\uc74c";
export const LEGACY_PROVIDER_LABEL =
  "\uae30\uc874 \uae30\ub85d: \ud504\ub85c\ubc14\uc774\ub354 \ubbf8\uae30\ub85d";

export const EMPTY_USAGE_TOTALS: UsageTotals = {
  turns: 0,
  prompt: 0,
  completion: 0,
  reasoning: 0,
  total: 0,
  costUsd: 0,
  estimated: 0,
};

export function emptyUsageLedger(): UsageLedger {
  return { daily: [], models: [], providers: [], pricingSources: [] };
}

export function buildUsageLedger(messages: Message[]): UsageLedger {
  const byDate = new Map<string, UsageRow>();
  const byModel = new Map<string, UsageBreakdownRow>();
  const byProvider = new Map<string, UsageBreakdownRow>();
  const byPricingSource = new Map<string, UsageBreakdownRow>();

  for (const message of messages) {
    if (message.sync.deletedAt) continue;
    for (const variant of message.variants) {
      const tokens = variant.tokens;
      if (!tokens) continue;

      const usage = usageFromTokens(tokens);
      const date = localDateKey(variant.createdAt);
      mergeIntoMap(byDate, date, date, usage);
      mergeIntoMap(byModel, variant.model ?? "unknown-model", variant.model ?? UNKNOWN_MODEL_LABEL, usage);

      const providerKey = tokens.providerId ?? tokens.providerKind ?? "unknown-provider";
      const providerLabel =
        tokens.providerName ?? tokens.providerId ?? tokens.providerKind ?? LEGACY_PROVIDER_LABEL;
      mergeIntoMap(byProvider, providerKey, providerLabel, usage);

      if (tokens.pricingSource) {
        mergeIntoMap(byPricingSource, tokens.pricingSource, tokens.pricingSource, usage);
      }
    }
  }

  return {
    daily: [...byDate.values()].sort((a, b) => b.date.localeCompare(a.date)),
    models: sortBreakdowns([...byModel.values()]),
    providers: sortBreakdowns([...byProvider.values()]),
    pricingSources: sortBreakdowns([...byPricingSource.values()]),
  };
}

export function sumUsageSince(rows: UsageRow[], sinceMs: number): UsageTotals {
  return rows
    .filter((row) => new Date(`${row.date}T23:59:59`).getTime() >= sinceMs)
    .reduce<UsageTotals>((acc, row) => {
      addUsage(acc, row);
      return acc;
    }, { ...EMPTY_USAGE_TOTALS });
}

function usageFromTokens(tokens: NonNullable<Message["variants"][number]["tokens"]>): UsageTotals {
  const prompt = tokens.prompt ?? 0;
  const completion = tokens.completion ?? 0;
  const reasoning = tokens.reasoning ?? 0;
  return {
    turns: 1,
    prompt,
    completion,
    reasoning,
    total: tokens.total ?? prompt + completion + reasoning,
    costUsd: tokens.totalCostUsd ?? 0,
    estimated: tokens.estimated ? 1 : 0,
  };
}

function mergeIntoMap<T extends UsageBreakdownRow | UsageRow>(
  map: Map<string, T>,
  key: string,
  labelOrDate: string,
  usage: UsageTotals,
) {
  const current =
    map.get(key) ??
    ({
      ...EMPTY_USAGE_TOTALS,
      ...(isDateKey(labelOrDate) ? { date: labelOrDate } : { key, label: labelOrDate }),
    } as T);
  addUsage(current, usage);
  map.set(key, current);
}

function addUsage(target: UsageTotals, usage: UsageTotals) {
  target.turns += usage.turns;
  target.prompt += usage.prompt;
  target.completion += usage.completion;
  target.reasoning += usage.reasoning;
  target.total += usage.total;
  target.costUsd += usage.costUsd;
  target.estimated += usage.estimated;
}

function sortBreakdowns(rows: UsageBreakdownRow[]): UsageBreakdownRow[] {
  return rows.sort((a, b) => b.costUsd - a.costUsd || b.total - a.total || a.label.localeCompare(b.label));
}

function localDateKey(time: number): string {
  const d = new Date(time);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isDateKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}
